import { throwError as observableThrowError, Observable, forkJoin } from "rxjs";

import { catchError, map, retry, switchMap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { of } from "rxjs";

import { Project } from "../../models/project";
import { AuthService } from "../../shared/services/auth.service";
import { JobDetail } from "../../models/jobDetail";
import { PlotData } from "../../models/plotData";
import { PlotDetail } from "../../models/plotDetail";
import { SliceType } from "../../models/sliceType";
import { HttpService } from "../../shared/services/http.service";
import { Cost } from "../../models/cost";
import { Coordinate } from "../../models/coordinate";
import { AppConfig } from "../../app.config";
import { ProjectDialog } from "../../models/projectDialog";
import { JobDetailDialog } from "../../models/jobDetailDialog";
import { JobStatusDialog } from "../../models/jobStatusDialog";
import { ShotOverlayDto } from "../../models/shotOverlayDto";
import { ModelType } from "../../models/modelType";
import { ShotOverlayType } from "../../models/shotOverlayType";
import { RunfileParam } from "../../models/runfileParamType";
import { inflate } from "pako";
import { HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { Field, NewField } from "../../models/field";
import { DisplayVariable } from "../../models/displayVariable";
import { G_JobHorizon } from "../../models/generalizedHorizon";
import { LogEntry, SummaryLogEntry } from "../../models/jobLog";
import { PaginatedResponse } from "./paginator";

@Injectable({
  providedIn: "root",
})
export class ProjectService {
  constructor(private http: HttpService, private authService: AuthService) {}

  getAllProjects(): Observable<
    { id: string; name: string; description: string; field?: string }[]
  > {
    return this.http.post(
      `${AppConfig.settings.apiUrl}/projects/search/`,
      {},
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  getProjectById(id: string): Observable<Project> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/projects/${id}/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return;
          let d = response;
          
          const displayVariablesMap: { [key: string]: { min: number | null, max: number | null } } = {};
          if (d.display_variables && Array.isArray(d.display_variables)) {
            d.display_variables.forEach((dv: any) => {
              displayVariablesMap[dv.key] = {
                min: dv.default_min_value,
                max: dv.default_max_value
              };
            });
          }

          var project: Project = {
            id: d.id,
            name: d.name,
            description: d.description,
            displayVariables: displayVariablesMap,
            field: d.field,
            archiveEnabled: d?.archive_enabled ?? false,
            horizons: d.horizons.map(h => ({...h, type: "Project"})) || [],
            jobs: d.jobs.map(jobMapper)
              // .map((j) => {
              //   return {
              //     id: j.id,
              //     name: j.job_name,
              //     createdDate: new Date(j.created),
              //     comments: j.comments,
              //     status: j.status,
              //     minVelocity: null,
              //     maxVelocity: null,
              //     minVelocityZ: null,
              //     maxVelocityZ: null,
              //     parent_job_id: Object.keys(j).includes("parent_job_id")
              //       ? j.parent_job_id
              //       : null,
              //   };
              // })
          };

          return this.setProjectDefaults(project);
        })
      );
  }

  getProjectTable(id: string, rebuild = false, ids = []): Observable<any> {
    let params = new URLSearchParams();
    params.set("rebuild", rebuild.toString());
    if (ids.length > 0) {
      params.set("ids", ids.join(","));
    }
    // console.log(`${AppConfig.settings.apiUrl}/projects/${id}/job_types/?${params.toString()}`)
    let headers = this.getAuthorizationHeader();
    // headers = headers.append(
    //   "Cache-Control",
    //   "no-cache, no-store, must-revalidate, post-check=0, pre-check=0"
    // );

    return this.http
      .get(
        `${
          AppConfig.settings.apiUrl
        }/projects/${id}/job_types/?${params.toString()}`,
        { headers: headers }
      )
      .pipe(
        map((response) => {
          if (!response) {
            console.error("getProjectTable: empty response");
            return;
          }

          let d = response;
          return d;
        })
      );
  }

  getParameterTable(id: string, rebuild = false, ids = []): Observable<any> {
    let params = new URLSearchParams();
    params.set("rebuild", rebuild.toString());
    if (ids.length > 0) {
      params.set("ids", ids.join(","));
    }
    let randomNumber = Number(Math.random()*100000).toFixed(0);
    params.set('_t', `${randomNumber}`)
    let headers = this.getAuthorizationHeader();
    // headers = headers.append(
    //   "Cache-Control",
    //   "no-cache, no-store, must-revalidate, post-check=0, pre-check=0"
    // );
    return this.http
      .get(
        `${
          AppConfig.settings.apiUrl
        }/projects/${id}/parameter_table/?${params.toString()}`,
        { headers: headers }
      )
      .pipe(
        map((response) => {
          if (!response) {
            console.error("getParameterTable: empty response");
            return;
          }

          let d = response;
          return d;
        })
      );
  }

  getProjectLevelEnvVars(id: string, rebuild = false, ids = []): Observable<any> {
    let params = new URLSearchParams();
    params.set("rebuild", rebuild.toString());
    if (ids.length > 0) {
      params.set("ids", ids.join(","));
    }
    let headers = this.getAuthorizationHeader();
    
    return this.http
      .get(
        `${
          AppConfig.settings.apiUrl
        }/projects/${id}/env-vars/?${params.toString()}`,
        { headers: headers }
      )
      .pipe(
        map((response) => {
          if (!response) {
            console.error("getEnvVarsTable: empty response");
            return;
          }

          let d = response;
          return d;
        })
      );
  }

  getProjectNetwork(id: string, FM = false): Observable<any> {
    let params = new URLSearchParams();
    params.set("FM", FM.toString());
    // console.log(`${AppConfig.settings.apiUrl}/projects/${id}/job_types/?${params.toString()}`)
    return this.http
      .get(
        `${
          AppConfig.settings.apiUrl
        }/projects/${id}/flowchart/?${params.toString()}`,
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) {
            console.error("getParameterTable: empty response");
            return;
          }

          let d = response;
          return d;
        })
      );
  }

  getProjectFlowchart(id: string, FM = false): Observable<any> {
    let params = new URLSearchParams();
    params.set("FM", FM.toString());
    // console.log(`${AppConfig.settings.apiUrl}/projects/${id}/job_types/?${params.toString()}`)
    return this.http
      .get(
        `${
          AppConfig.settings.apiUrl
        }/projects/${id}/flowchart_v2/?${params.toString()}`,
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) {
            console.error("getParameterTable: empty response");
            return;
          }

          let d = response;
          return d;
        })
      );
  }

  getProjectTableCSV(id: string): Observable<any> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/projects/${id}/job_types/csv/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) {
            console.error("getProjectTableCSV: empty response");
            return;
          }

          let d = response;
          return d;
        })
      );
  }

  getJobDetail(id: string): Observable<JobDetail> {
    let randomNumber = Number(Math.random()*100000).toFixed(0);
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/?_t=${randomNumber}`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return;

          let j = response;
          var jobDetail = {
            projectId: j.project.id,
            projectName: j.project.name,
            projectDescription: j.project.description,
            projectMinModelDisplayValue: j.project.min_model_display_value,
            projectMaxModelDisplayValue: j.project.max_model_display_value,
            id: j.id,
            name: j.job_name,
            createdDate: new Date(j.created),
            status: j.status,
            basePath: j.job_basepath,
            originalPath: j.job_originalpath,
            comments: j.comments,
            prefix: j.job_prefix,
            lastParamModified: new Date(j.last_param_modified),
            lastStatusModified: new Date(j.last_status_modified),
            lastErrorStatus: j.last_error_status,
            started: j.started,
            last_used_palette: j.last_used_palette,
            modelGrid: {
              id: j.model_grid.id,
              name: j.model_grid.name,
              description: j.model_grid.description,
              unit: j.model_grid.unit,
              dx: j.model_grid.dx,
              is2d: j.model_grid.is_2d,
              // TODO: apparently nx1 and nx2 are flipped in most "typical" models
              // need to investivate inline/crossline mapping to nx1/nx2 throughout the code
              // to clear up inconsistencies
              nx1: j.x2_is_inline ? j.model_grid.nx1 : j.model_grid.nx2,
              nx2: j.x2_is_inline ? j.model_grid.nx2 : j.model_grid.nx1,
              nx3: j.model_grid.nx3,
            },
            padding: {
              id: j.model_padding.id,
              top: j.model_padding.top,
              left: j.model_padding.left,
              right: j.model_padding.right,
              bottom: j.model_padding.bottom,
              front: j.model_padding.front,
              back: j.model_padding.back,
            },
            useInterblocks: j.use_iterblocks,
            iterations: j.iterations,
            iterationsComplete: j.iterations_completed,
            defaultNx1: null,
            
            defaultNx2: null,
            defaultNx3: null,
            containsLog: j.project.well_logs && j.project.well_logs.length > 0,
            blockNumber: null,
            frequency: null,
            x2_is_inline: j.x2_is_inline,
            startTime: !!j.last_started
              ? new Date(j.last_started.split("+")[0] + ".000Z")
              : null,
            runTime: j.cumulated_runtime_sec,
            wellLogs: j.project.well_logs.map((i) => {
              return {
                id: i.id,
                name: i.name,
                inline: i.inline_number,
                crossline: i.crossline_number,
              };
            }),
            tag: j.tag,
            parent_job_id: j.parent_job_id,
            model_code: j.model_code,
            parent_cp_num: j.parent_cp_num,
            epicmod_comments: Object.keys(j).includes("epicmod_comments")
              ? j.epicmod_comments
              : JSON.stringify({
                  EPICMod_ModelProps: "",
                  EPICMod_Update: "",
                }),
            keep_job: j.keep_job,
            is_archived: j.is_archived,
            storage_cost: j.storage_cost,
            job_type: j.type,
            soft_archive_status: j.soft_archive_status,
            functional_sequence: j.functional_sequence,
            offshoot: j.offshoot,
            type: j.type,
            run_cost : j.run_cost,
            run_cost_type: j.run_cost_type,
            owner: j.owner,
            is_aws: j.is_aws
          };

          this.setModelDefaults(jobDetail);
          return jobDetail;
        })
      );
  }

  getJobIterationsCompleted(id: string): Observable<number> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((res) => {
          if (!res) {
            return null;
          }
          res = res;
          return res.iterations_completed;
        })
      );
  }

  getJobMeta(id: string): Observable<any> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/meta/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return;
          return response;
        })
      );
  }

  getRunfile(id: string, iteration: number = 0): Observable<any> {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams();
    if (!!iteration) {
      params = params.append("iteration", iteration.toString());
    }
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/runfile/`, {
        headers,
        params,
      })
      .pipe(
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((error) => of(null))
      );
  }

  getRunfileObject(id: string, keywords: string[] = null): Observable<any> {
    let headers = this.getAuthorizationHeader();
    let query_str = "";
    if (keywords.length > 0) {
      let params = new URLSearchParams();
      params.set("keywords", keywords.join(","));
      query_str = "?" + params.toString();
    }

    return this.http
      .get(
        `${AppConfig.settings.apiUrl}/jobs/${id}/runfile_object/${query_str}`,
        { headers }
      )
      .pipe(
        map((response) => {
          if (!response) return null;
          return response;
        }),
        catchError((error) => of(null))
      );
  }

  getPlotData(id: string): Observable<PlotData> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/plot_data/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response: any) => {
          if (response) {
            try {
              if (!response) {
                console.log("response has empty data");
                return;
              }
            } catch (e) {
              //response has empty data
              console.log("response has empty data");
              return;
            }
          }
          let j = response;
          if (j.cpnums.length <= 0) return;

          if (Object.keys(j).includes("cpnums_with_data")) {
            let cpnums_with_data = j.cpnums_with_data;
            delete j["cpnums_with_data"];
            if (cpnums_with_data.length < j.cpnums.length) {
              for (let cpnum of j.cpnums) {
                if (!cpnums_with_data.includes(cpnum)) {
                  j.fnl1.splice(cpnum - 1, 0, null);
                  j.stepfct.splice(cpnum - 1, 0, null);
                }
              }
            }
          }
          let plotData: PlotData = {
            cpnums: j.cpnums,
            fnl1: { data: j.fnl1 },
            stepfct: { data: j.stepfct },
          };
          delete j["cpnums"];
          delete j["fnl1"];
          delete j["stepfct"];
          if (j.stephis) {
            let sortedStepHists = [];
            for (let _i in plotData.cpnums) {
              let iteration = plotData.cpnums[_i];
              let i = parseInt(_i);
              let steps = j.stephis[iteration];
              if (!steps) {
                steps = [[], []];
              }
              steps[0]?.splice(0, 0, 0);
              steps[1]?.splice(0, 0, plotData.fnl1.data[i]);
              steps[2] = plotData.stepfct.data[i];
              sortedStepHists.push(steps);
            }
            plotData.step_history = sortedStepHists;
            delete j["stephis"];
          }
          let trace_fit = j.traces_fit || [];
          // there wouldn't be nulls in the cpnums array, but there could be nulls in the traces_fit arrays
          // so the length of tracefit arrays is bound to be greater or equal to the length of cpnums array
          if (
            trace_fit.length == 2 &&
            trace_fit[0].length >= plotData.cpnums.length
          ) {
            plotData.traces_fits = [];
            for (let key in j) {
              if (
                key === "norm_ratio" ||
                key === "fitting_factor" ||
                !j[key][0].some((d) => !!d)
              ) {
                continue;
              }
              let name = key.split("_").join(" ").replace("dc", "traces fit");
              // since the traces_fit array might be longer than the cpnums array, we need to filter out the nulls
              let data = j[key][0];
              let std = j[key][1];
              plotData.traces_fits.push({ name: name, data: data, std: std });
            }
          }
          return plotData;
        }),
        catchError((error) => {
          if (error.status == 404) {
            return of(null);
          } else {
            throw error;
          }
        })
      );
  }

  getMatchedJobs(
    rules,
    page: number = 1,
    sort: string = "_score"
  ): Observable<any> {
    // let requestOptions = new HttpHeaders();
    // requestOptions.append(
    //   "Authorization",
    //   this.authService.getTokenHeaderText()
    // );
    rules["page"] = page;
    rules["sort"] = sort;
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/params_search/`, {
        params: rules,
        headers: this.getAuthorizationHeader(),
        responseType: "json",
      })
      .pipe(
        map((response) => {
          if (!response) return;
          let searchResult = response;
          return searchResult;
        })
      );
  }

  getSearchResults(rules): Observable<any> {
    // let requestOptions = new Headers();
    // requestOptions.append(
    //   "Authorization",
    //   this.authService.getTokenHeaderText()
    // );
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/search_groups/`, {
        params: rules,
        headers: this.getAuthorizationHeader(),
        responseType: "json",
      })
      .pipe(
        map((response) => {
          if (!response) return;
          let searchResult = response;
          return searchResult.results;
        }),
        catchError((error) => {
          return of(error);
        })
      );
  }

  getMultipleJobs(jobIds, forceRefresh: boolean = false): Observable<any> {
    var url = `${AppConfig.settings.apiUrl}/jobs/group/`
    if (forceRefresh){
      // this is simply a cache-busting parameter
      let randomNumber = Number(Math.random()*100000).toFixed(0);
      url += `?_t=${randomNumber}`
    }
    return this.http
      .post(
        url,
        { jobs: jobIds },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          let dataList = response;
          return dataList.job_list.map(jobMapper);
        })
      );
  }

  getGroupData(jobs: any): Observable<any> {
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/jobs/group/`,
        { jobs: jobs },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          return response;
        })
      );
  }

  getGroupParams(jobs: any, keywords: any): Observable<any> {
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/jobs/group_params/`,
        { jobs: jobs, keywords: keywords },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          return response.params;
        })
      );
  }

  createSearchResultGroup(
    rule: any,
    jobIds: any,
    comment: string,
    name: string
  ): Observable<any> {
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/jobs/search_groups/`,
        { rule: rule, jobs: jobIds, comment: comment, name: name },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response || response) return;
          return response.group;
        }),
        catchError((error) => observableThrowError(error))
      );
  }

  updateSearchResultGroup(
    groupId: any,
    comments: string,
    name: string
  ): Observable<any> {
    let data = { commetns: comments, name: name };
    return this.http
      .patch(
        `${AppConfig.settings.apiUrl}/jobs/search_groups/${groupId}/`,
        data,
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          return groupId;
        })
      );
  }

  getModelTypes(jobId: string, iteration: number): Observable<ModelType[]> {
    return this.http
      .get(
        `${AppConfig.settings.apiUrl}/jobs/${jobId}/plot/types_available/${iteration}/`,
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          let data = response.types_available;
          let types_with_processing = response.type_pre_plot_processings;

          return Object.keys(types_with_processing).map((d) => {
            return {
              id: d,
              name: d,
              processing: types_with_processing[d],
            };
          });
        })
      );
  }

  private setProjectDefaults(project: Project): Project {
    if (project.id == "8a298703-d84f-48ca-aa23-100069ef7407") {
      project.minVelocity = 1500;
      project.maxVelocity = 3800;
    }

    if (project.id == "4ad49fe5-a624-4a3e-91e8-19fe8d4267ba7") {
      project.minVelocity = 1500;
      project.maxVelocity = 4500;
    }

    return project;
  }

  private setModelDefaults(job: JobDetail) {
    var janz = [
      "e64823c8-ee5c-4e9c-afce-044cf7e8cb1a",
      "db5b0dc1-d70d-49d6-81ce-988d9b9e2509",
      "509a95f9-a881-4dd1-be4e-8463f1953d53",
    ];
    if (janz.find((id) => job.id == id)) {
      job.defaultNx1 = 80;
      job.defaultNx2 = 448;
      job.defaultNx3 = 92;

      job.blockNumber = [1, 2, 3, 4, 5, 6, 7, 8];
      job.frequency = [4, 5, 6, 8, 10, 12, 14, 17];
      return;
    }
    var seg = [
      "1ef2c0f0-6da7-4d59-849f-b5b8c2370283",
      "67943a66-8b59-4f19-84cf-00a1052cfc92",
      "cb99daaf-dea5-4ce4-a34e-69189423ca11",
    ];
    if (seg.find((id) => job.id == id)) {
      job.blockNumber = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      job.frequency = [3.0, 3.3, 3.6, 4.0, 4.5, 5.2, 6.1, 7.3, 8.6, 10.0];

      if (job.id == "cb99daaf-dea5-4ce4-a34e-69189423ca11") {
        job.blockNumber = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
        job.frequency = [
          10, 11, 12.1, 13.3, 14.6, 16.1, 17.1, 19.5, 21.4, 23.6,
        ];
      }
    }

    job.defaultNx1 = Math.floor(job.modelGrid.nx1 / 2);
    job.defaultNx2 = Math.floor(job.modelGrid.nx2 / 2);
    job.defaultNx3 = Math.floor(job.modelGrid.nx3 / 2);
  }

  getSlice(
    id: string,
    modelType: string,
    iteration: number,
    sliceType: SliceType,
    sliceNumber: number,
    is2D: boolean,
    palette: string = null,
    minVelocity: number = null,
    maxVelocity: number = null,
    shotNumber: number = null,
    fwd: number = null,
    vpRef: number = null,
    agcWindow: number = null,
    timeSlice: number = null,
    forceReplot: boolean = false,
  ): Observable<PlotDetail> {
    const params: { [key: string]: string | number } = {};
    if (palette) params["palette"] = palette;

    // confirm both are not null
    if (minVelocity !== null && maxVelocity !== null) {
      // confirm that atleast 1 of then is non zero
      if (minVelocity !== 0 || maxVelocity !== 0) {
        // confirm that neither is NaN
        if (!(isNaN(minVelocity) || isNaN(maxVelocity))) {
          params["min_val"] = minVelocity;
          params["max_val"] = maxVelocity;
        }
      }
    }

    if (modelType) {
      // if (modelType == "Delta" || modelType == "Epsilon") iteration = 0;
      params["type"] = modelType;
    }

    if (shotNumber) params["shot_id"] = shotNumber;

    if (timeSlice) params["time_slice"] = timeSlice;

    if (fwd) params["fwd"] = fwd;

    if (!is2D) {
      params["slice_dim"] = sliceType;
      params["slice_number"] = sliceNumber;
    }

    if (modelType == "Total_Vp_Update" && vpRef) params["vp_ref"] = vpRef;

    if (
      (modelType == "DenChange AGC" ||
        modelType == "Density plus DenChange Reflectivity AGC") &&
      agcWindow
    ) {
      params["agc_window"] = agcWindow;
    }

    params["boto3"] = "true";
    // params["force_replot"] = "true";

    var url = `${AppConfig.settings.apiUrl}/jobs/${id}/plot/${iteration}/`;

    return this.http
      .get(url, { headers: this.getAuthorizationHeader(), params })
      .pipe(
        map((response) => {
          if (!response) return;
          let p = response;
          return {
            url: p.png_plot_url,
            colorBarUrl: p.png_colorbar_url,
            min: p.min_value,
            max: p.max_value,
            sliceUrl: p.model_slice_url,
            trueMin: p.true_min_value,
            trueMax: p.true_max_value,
          };
        }),
        catchError((err) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  getSliceArray(
    url: string,
    width: number,
    height: number,
    min: number,
    max: number
  ): Observable<Array<Array<number>>> {
    return this.http
      .get(url, {
        headers: this.getAuthorizationHeader(),
        responseType: "arraybuffer",
      })
      .pipe(
        map((res: ArrayBuffer) => {
          var arrayBuf = res;
          var int16Array = new Uint16Array(
            arrayBuf,
            0,
            Math.floor(arrayBuf.byteLength / 2)
          );
          var precision = 100;
          if (max < 1 && max > 0) {
            precision = 1000;
            let val = max;
            do {
              val = val * 10;
              precision = precision * 10;
            } while (val < 1);
          }
          var array = new Array();
          for (var i = 0; i < height; i++) {
            array.push(
              Array.prototype.slice
                .call(int16Array.slice(i * width, (i + 1) * width))
                .map(
                  (d) =>
                    Math.round(precision * ((d / 65535) * (max - min) + min)) /
                    precision
                )
            );
          }
          return array;
        }),
        catchError((err: HttpErrorResponse) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  getRanges(
    id: string,
    modelType: string,
    iteration: number,
    sliceParams: object,
    shot: number = null,
    traceSlice: number = null
  ): Observable<{ min_val: number; max_val: number }> {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams();
    // header.params = sliceParams;
    // header.params["type"] = modelType;
    // if (shot) header.params["shot_id"] = shot;
    // if (traceSlice) header.params["trace_slice"] = traceSlice;
    for (let [key, val] of Object.entries(sliceParams)) {
      params = params.append(key, val.toString());
    }
    params = params.append("type", modelType.toString());
    if (shot) params = params.append("shot_id", shot.toString());
    if (traceSlice)
      params = params.append("trace_slice", traceSlice.toString());
    params = params.append("boto3", "true");
    
    return this.http
      .get(
        `${AppConfig.settings.apiUrl}/jobs/${id}/plot/${iteration}/ranges/`,
        { headers, params }
      )
      .pipe(
        map((response: { min_val: number; max_val: number }) => {
          if (!response) return null;
          return response;
        }),
        catchError((error) => {
          return observableThrowError(error);
        })
      );
  }

  getSonicLog(id: string, frequency?: number): Observable<{log: Array<Coordinate>, markers: Array<{}>}> {
    let params = new HttpParams();
    if (frequency !== undefined && frequency !== null && frequency > 0) {
      params = params.set('frequency', frequency.toString());
    }
    return this.http
      .get(`${AppConfig.settings.apiUrl}/well-logs/${id}/`, {
        headers: this.getAuthorizationHeader(),
        params: params
      })
      .pipe(
        map((response) => {
          if (!response) return;
          const log = response.log.map((d) => {
            return { x: d.z, y: d.v };
          });
          return {log, markers: response.markers}
        }),
        catchError((err) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  getLog(id: string): Observable<string> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/log/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return;

          return response.toString();
        })
      );
  }

  getCosts(
    id: string,
    totalIterations: number,
    switched: boolean = false
  ): Observable<Cost> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/detail/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return;

          let data = response;
          if (!data.shot_info || data.shot_info.length == 0) return null;

          var costItems = data.shot_info
            .sort((a, b) => b.c_total - a.c_total)
            .map((d) => {
              var iterations =
                (data.shots_per_iter * totalIterations) / data.total_shots;
              var runTime = Math.max(0.02, (d.c_total / 9229212) * 3 * 0.057);

              return {
                shotNo: d.shot_id,
                cnx: d.cnx,
                cny: d.cny,
                cnz: d.cnz,
                sx: switched ? d.sy : d.sx,
                sy: switched ? d.sx : d.sy,
                sz: d.sz,
                timeSamples: data.time_steps,
                runTime: Math.round(runTime * 1000) / 1000,
                iterations: Math.ceil(iterations),
                nodeHours: iterations * runTime,
                cost: iterations * runTime * 0.55,
              };
            });

          return {
            formulationType: data.formulation_type,
            shotsPerIteration: data.shots_per_iter,
            timeSample: data.time_sample,
            timeSteps: data.time_steps,
            totalBlocks: data.total_blocks,
            totalShots: data.total_shots,
            costItems: costItems,
            vmax: data.vmax,
            type: data.type,
          };
        })
      );
  }

  getHorizonTypes(id: string): Observable<G_JobHorizon[]> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/horizon/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        retry(2),
        map((response: {horizons_available: string[]} ) => {
          if (!response) return null;

          var data = response;
          if (!data || !data.horizons_available) return null;

          return data.horizons_available.sort().map(h => ({
            id: h,
            name: h,
            type: 'Job'
          }));
        })
      );
  }

  getHorizon(
    id: string,
    sliceNumber: number,
    sliceType: SliceType,
    horizonName: string
  ): Observable<number[]> {
    // horizonName is the raw name; encoding is handled by the params option.
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/horizon/`, {
        headers: this.getAuthorizationHeader(),
        params: {
          name: horizonName,
          slice_dim: sliceType,
          slice_number: sliceNumber,
        },
      })
      .pipe(
        retry(2),
        map((response) => {
          if (!response) return;
          return response;
        })
      );
  }

  getShotOverlay(id: string, iteration: number): Observable<ShotOverlayDto[]> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/shot_fit/${iteration}/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response: any): ShotOverlayDto[] => {
          if (!response) return [];
          return response.shot_fit_info ?? [];
        }),
        catchError((err) => {
          throw Error(
            err.error?.detail ||
              err.error?.error ||
              err.message ||
              "An unexpected error occurred",
          );
        })
      );
  }

  getFnlcRanges(id: string): Observable<any> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/fnlc_range/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return
          let range = response;
          return { min_val: range.min_val, max_val: range.max_val };
        })
      );
  }

  getShotOverlayTypes(
    id: string,
    iteration: number = 1,
    model_page: boolean = false
  ): Observable<ShotOverlayType[]> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/shot_fit/${iteration}/?model_page=${String(model_page)}`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return;
          var types = [
            { id: "dc_low", name: "Traces Fit - Low", min: 0, max: 100 },
            { id: "dc_raw", name: "Traces Fit - Raw", min: 0, max: 100 },
            { id: "traces_fit", name: "Traces Fit", min: 0, max: 100 },
            { id: "traces_fit", name: "Traces Fit", min: 0, max: 100 },
            { id: "norm_ratio", name: "Norm Ratio", min: 0, max: 1 },
            { id: "fitting_factor", name: "Fitting Factor", min: 0, max: 1 },
          ];
          var fitTypes = response.fit_types;
          if (!fitTypes) return null;

          return Object.keys(fitTypes).map((d) => {
            return {
              id: d,
              name: fitTypes[d],
              min: types.find((t) => t.id === d)
                ? types.find((t) => t.id === d).min
                : null,
              max: types.find((t) => t.id === d)
                ? types.find((t) => t.id === d).max
                : null,
            };
          });
        })
      );
  }

  getShotsAvailableForModel(
    id: string,
    iteration: number,
    modelType: string = null,
    timeSlice: number = null
  ): Observable<number[]> {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams();

    params = params.append("iteration", iteration.toString());
    // header.params = { iteration: iteration };
    if (modelType) {
      params = params.append("type", modelType);
      // header.params["type"] = modelType;
    }
    if (timeSlice) {
      params = params.append("time_slice", timeSlice.toString());
      // header.params["time_slice"] = timeSlice;
    }
    return this.http
      .get(
        `${AppConfig.settings.apiUrl}/jobs/${id}/plot/shots_available/${iteration}/`,
        { headers, params, responseType: "json" }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          return response.data;
        }),
        catchError((err) => {
          if (err.status == 404) {
            return of(null);
          }
          return observableThrowError(err);
        })
      );
  }

  getTimeSliceAvailableForModel(
    id: string,
    iteration: number,
    shot_id: number,
    modelType: string
  ): Observable<any> {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams().append("types", modelType);
    return this.http
      .get(
        `${AppConfig.settings.apiUrl}/jobs/${id}/plot/time_slice_available/${iteration}/${shot_id}/`,
        { headers, params }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          let timeSlices = response.data;
          timeSlices.sort((a, b) => a - b);
          return timeSlices;
        })
      );
  }

  getTraceFilesByIteration(
    id: string,
    iteration: number
  ): Observable<number[]> {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams().append("iteration", iteration.toString());
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/shot_files/`, {
        headers,
        responseType: "json",
        params,
      })
      .pipe(
        map((response) => {
          if (!response) return;
          return response.data;
        }),
        catchError((err) => {
          if (err.status == 404) {
            return of(null);
          }
          return observableThrowError(err);
        })
      );
  }

  getTraceFilesById(id: string, data: object): Observable<number[]> {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams();
    for (let [key, val] of Object.entries(data))
      params = params.append(key, val?.toString());

    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/shot_files/`, {
        headers,
        responseType: "json",
        params,
      })
      .pipe(
        map((response) => {
          if (!response) return;
          return response.data;
        }),
        catchError((err) => {
          if (err.status == 404) {
            return of(null);
          }
          return observableThrowError(err);
        })
      );
  }

  getTraceMeta(id: string, data: object): Observable<any> {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams();
    for (let [key, val] of Object.entries(data))
      params = params.append(key, val.toString());
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/shot_meta/`, {
        headers,
        params,
      })
      .pipe(
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((err) => {
          if (err.status == 404) {
            return of(null);
          }
          return observableThrowError(err);
        })
      );
  }

  getRunfileParameters(
    id: string,
    setDefault: boolean
  ): Observable<RunfileParam> {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams().append("default", setDefault.toString());
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/parameters/`, {
        headers,
        params,
      })
      .pipe(
        map((response) => {
          if (!response) return;
          let data = response;
          return {
            global: data.global,
            iterBlks: data.iteration_blocks,
          };
        }),
        catchError((err) => {
          if (err.status == 404 || err.status == 500) {
            return of(null);
          }
          return observableThrowError(err);
        })
      );
  }

  getScreenShots(id: string): Observable<string[]> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/screenshots`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((res) => {
          if (!res) return;
          return res.screenshots;
        })
      );
  }

  getRecentSearchGroups(): Observable<any> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/recent_searches/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((res) => {
          if (!res) return;
          return res.recent_groups;
        })
      );
  }

  getAvailableCustomNames(id: string, iteration: number, shot_id: number) {
    let headers = this.getAuthorizationHeader();
    let params = new HttpParams()
      .append("iteration", iteration.toString())
      .append("shot_id", shot_id.toString());
    // header.params = { iteration: iteration, shot_id: shot_id };
    return this.http
      .get(
        `${AppConfig.settings.apiUrl}/jobs/${id}/shot/custom_name_available/`,
        { headers, params }
      )
      .pipe(
        map((res) => {
          if (!res) return;
          return res.available_custom_types;
        })
      );
  }

  getRcvrData(
    id: string,
    iteration: number,
    shot_id: number,
    fwd: number,
    filterParams: any = {},
    x2IsInline: boolean = false
  ) {
    // let header = this.getAuthorizationHeader();
    // header.params = { iteration: iteration, shot_id: shot_id, fwd: fwd };
    // Object.assign(header.params, filterParams);
    // header.responseType = ResponseContentType.ArrayBuffer;
    let headers = this.getAuthorizationHeader();

    let params = new HttpParams();
    params = params.append("iteration", iteration.toString());
    params = params.append("shot_id", shot_id.toString());
    params = params.append("fwd", fwd?.toString());
    for (const [key, val] of Object.entries(filterParams)) {
      if (Array.isArray(val)) {
        for (let v of val) {
          params = params.append(key, v.toString());
        }
      } else {
        params = params.append(key, val.toString());
      }
    }
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/rcvr_list/`, {
        headers,
        params,
        responseType: "arraybuffer",
      })
      .pipe(
        map((res: ArrayBuffer) => {
          if (!res) return;

          let inflatedData: Uint8Array = inflate(res, { to: "int8" });
          let dataArray = inflatedData.buffer;
          let metas = new Uint32Array(dataArray, 0, 6);
          // 0: num rcvr, 1: num selects, 2: src off, 3: rcvr off, 4: select off, (5: data off) -> deleted
          let groupFunc = (a, c, i) => {
            i % 2 ? a[~~(i / 2)].push(c) : a.push([c]);
            return a;
          };
          let groupFunc3d = (a, c, i) => {
            i % 3 ? a[~~(i / 3)].push(c) : a.push([c]);
            return a;
          };
          let src_locs = Array.from(
            new Float32Array(dataArray.slice(metas[2], metas[3]))
          );
          // console.log(src_locs)
          let rcvr_locs;
          if (src_locs.length == 3) {
            rcvr_locs = Array.from(
              new Float32Array(dataArray.slice(metas[3], metas[4]))
            ).reduce(groupFunc3d, []);
          } else {
            rcvr_locs = Array.from(
              new Float32Array(dataArray.slice(metas[3], metas[4]))
            ).reduce(groupFunc, []);
          }
          if (x2IsInline) {
            if (src_locs.length == 3) {
              [src_locs[0], src_locs[1], src_locs[2]] = [
                src_locs[1],
                src_locs[0],
                src_locs[2],
              ];
              rcvr_locs.forEach(
                (r) => ([r[0], r[1], r[2]] = [r[1], r[0], r[2]])
              );
            } else {
              [src_locs[0], src_locs[1]] = [src_locs[1], src_locs[0]];
              rcvr_locs.forEach((r) => ([r[0], r[1]] = [r[1], r[0]]));
            }
          }
          // console.log(src_locs)
          // console.log(rcvr_locs)
          return {
            src_loc: src_locs,
            rcvr_loc: rcvr_locs,
            selected_idx: Array.from(
              new Uint32Array(dataArray.slice(metas[4], metas[5]))
            ),
            values: Array.from(
              new Float32Array(
                dataArray.slice(metas[5], metas[5] + metas[1] * 4)
              )
            ),
          };
        })
      );
  }

  getRcvrData_data(
    id: string,
    iteration: number,
    shot_id: number,
    fwd: number,
    filterParams: any = {},
    x2IsInline: boolean = false
  ) {
    let headers = this.getAuthorizationHeader();

    let params = new HttpParams();
    params = params.append("iteration", iteration.toString());
    params = params.append("shot_id", shot_id.toString());
    params = params.append("fwd", fwd?.toString());
    for (const [key, val] of Object.entries(filterParams)) {
      if (Array.isArray(val)) {
        for (let v of val) {
          params = params.append(key, v.toString());
        }
      } else {
        params = params.append(key, val.toString());
      }
    }
    // headers.params = { iteration: iteration, shot_id: shot_id, fwd: fwd };
    // Object.assign(headers.params, filterParams);

    // let responseType = ResponseContentType.ArrayBuffer;

    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/rcvr_data/`, {
        headers,
        params: params,
        responseType: "arraybuffer",
      })
      .pipe(
        map((res) => {
          if (!res) return [];
          let inflatedData: Uint8Array = inflate(res);
          let dataArray = inflatedData.buffer;
          return Array.from(new Float32Array(dataArray));
        })
      );
  }

  getSearchKeywords() {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/search_keys/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((res) => {
          if (!res) return;
          return res.keywords;
        })
      );
  }

  getFilesList(id: string, base_path: string) {
    let param = new URLSearchParams();
    param.set("path", base_path);
    let headers = this.getAuthorizationHeader();
    // header.params = {path:base_path};
    return this.http
      .get(
        `${
          AppConfig.settings.apiUrl
        }/jobs/${id}/files_list?${param.toString()}`,
        { headers }
      )
      .pipe(
        map((res) => {
          if (!res) return;
          return res.result;
        })
      );
  }

  getFunctionalSequence(id: string) {
    let header = { headers: this.getAuthorizationHeader() };
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/jobs/${id}/functional_sequence/`,
        {},
        header,
      )
      .pipe(
        map((res) => {
          if (!res || !res) return null;
          return res;
        }),
      );
  }

  getEnvVariables(id: string) {
    let headers = this.getAuthorizationHeader();
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${id}/env_vars/`, { headers })
      .pipe(
        map((res) => {
          if (!res) return null;
          return res;
        })
      );
  }

  refreshEnvVariables(id: string): Observable<any> {
    let headers = this.getAuthorizationHeader();
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/jobs/${id}/env_vars/`,
        {},
        {
          headers,
        }
      )
      .pipe(
        map((res) => {
          if (!res) return null;
          return res;
        })
      );
  }

  createProject(data: ProjectDialog): Observable<any> {
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/projects/`,
        {
          name: data.name,
          description: data.description,
          archive_enabled: data.archiveEnabled || false,
          is_parameter_scan: data.isParameterScan || false,
          jobs: [],
          well_logs: [],
          field: data.field,
        },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((err) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  updateProjectDetails(data: ProjectDialog): Observable<any> {
    return this.http
      .patch(
        `${AppConfig.settings.apiUrl}/projects/${data.id}/`,
        {
          name: data.name,
          description: data.description,
          field: data.field?.id || null,
        },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        retry(2),
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((err) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  getProjectDatatable(projectId: string): Observable<any> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/projects/${projectId}/data_table`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((err) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  rebuildProjectDatatable(projectId: string, body: object): Observable<any> {
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/projects/${projectId}/data_table/`,
        body,
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((err) => observableThrowError(err))
      );
  }

  updateProjectDatatable(projectId: string, body: object): Observable<any> {
    return this.http
      .patch(
        `${AppConfig.settings.apiUrl}/projects/${projectId}/data_table/`,
        body,
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((err) => observableThrowError(err))
      );
  }

  deleteProjectDatatableRecord(
    projectId: string,
    body: object
  ): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.append(
      "Authorization",
      this.authService.getTokenHeaderText()
    );
    const options = { body: body, headers: headers };

    return this.http
      .delete(
        `${AppConfig.settings.apiUrl}/projects/${projectId}/data_table/`,
        options
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((err) => observableThrowError(err))
      );
  }

  getModelTableByProject(projectId: string): Observable<any> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/projects/${projectId}/model_table/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          let details = err.message;
          return observableThrowError(details);
        })
      );
  }

  rebuildModelTable(projectId: string): Observable<any> {
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/projects/${projectId}/model_table/`,
        {},
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((err: HttpErrorResponse) => observableThrowError(err))
      );
  }

  updateModeltable(projectId: string, body: object): Observable<any> {
    return this.http
      .patch(
        `${AppConfig.settings.apiUrl}/projects/${projectId}/model_table/`,
        body,
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((err: HttpErrorResponse) => observableThrowError(err))
      );
  }

  getProjectJobs(projectId: string, body: object): Observable<any> {
    return this.http
      .post(`${AppConfig.settings.apiUrl}/projects/${projectId}/jobs/`, body, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((err: HttpErrorResponse) => observableThrowError(err))
      );
  }

  deleteJob(id: string): Observable<any> {
    return this.http
      .delete(`${AppConfig.settings.apiUrl}/jobs/${id}/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        retry(2),
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          let details = err.message;
          return observableThrowError(details);
        })
      );
  }

  deleteSearchGroup(id: string): Observable<any> {
    return this.http
      .delete(`${AppConfig.settings.apiUrl}/jobs/search_groups/${id}/`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return;
          return id;
        })
      );
  }

  deleteScreenshot(id: string): Observable<any> {
    return this.http
      .delete(`${AppConfig.settings.apiUrl}/screenshot/${id}`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response) => {
          if (!response) return;
          return id;
        })
      );
  }

  updateJobDetails(data: JobDetailDialog): Observable<any> {
    return this.http
      .patch(
        `${AppConfig.settings.apiUrl}/jobs/${data.id}/`,
        {
          job_name: data.name,
          project: data.projectId,
          job_basepath: data.basePath,
          job_originalpath: data.originalPath,
          status: data.status,
          iterations: data.iterations,
          iterations_completed: data.iterationsComplete,
          comments: data.comments,
          tag: data.tag,
          parent_job_id: data.parent_job_id,
          parent_cp_num: data.parent_cp_num,
          keep_job: data.keep_job,
          offshoot: data.offshoot,
          owner: data.owner,
          is_aws: data.is_aws,
          type: data.type
        },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        retry(2),
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  updateJobEpicModComments(
    id: string,
    epicmod_comments: { [key: string]: string }
  ) {
    return this.http
      .patch(
        `${AppConfig.settings.apiUrl}/jobs/${id}/`,
        { epicmod_comments: JSON.stringify(epicmod_comments) },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        retry(2),
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  refreshJobDetails(job_id: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.append(
      "Authorization",
      this.authService.getTokenHeaderText()
    );

    // this is simply a cache-busting parameter
    let randomNumber = Number(Math.random()*100000).toFixed(0);
    return this.http
      .get(`${AppConfig.settings.apiUrl}/jobs/${job_id}?_t=${randomNumber}`, { headers })
      .pipe(
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          let details = err;
          return observableThrowError(details);
        })
      );
  }

  updateJobStatus(data: JobStatusDialog): Observable<any> {
    return this.http
      .patch(
        `${AppConfig.settings.apiUrl}/jobs/${data.id}/`,
        {
          status: data.status,
        },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        retry(2),
        map((response) => {
          if (!response) return;
          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          let details = err;
          return observableThrowError(details);
        })
      );
    // .catch((err: ) => {
    //   let details = err;
    //   return observableThrowError(details);
    // });
  }

  updateScreenshotCaption(id: string, caption: string): Observable<any> {
    return this.http.patch(
      `${AppConfig.settings.apiUrl}/screenshot/${id}`,
      { caption: caption },
      { headers: this.getAuthorizationHeader() }
    );
  }

  uploadScreenshots(id: string, data: any): Observable<any> {
    return this.http
      .post(
        `${AppConfig.settings.apiUrl}/jobs/${id}/screenshots/`,
        { screenshots: data },
        { headers: this.getAuthorizationHeader() }
      )
      .pipe(
        map((response) => {
          if (!response) return;
          return response;
        })
      );
  }

  getAllProjectsCosts(): Observable<any> {
    return this.http.get(`${AppConfig.settings.apiUrl}/costs`, {
      headers: this.getAuthorizationHeader(),
    });
  }

  setProjectArchiving(data: object): Observable<any> {
    return this.http.patch(`${AppConfig.settings.apiUrl}/costs`, data, {
      headers: this.getAuthorizationHeader(),
    });
  }

  getModelInformation(jobId: string, iteration: number): Observable<any> {
    return this.http.get(
      `${AppConfig.settings.apiUrl}/jobs/${jobId}/plot/${iteration}/information/`,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  createJobGroups(body: object): Observable<any> {
    return this.http.post(
      `${AppConfig.settings.apiUrl}/projects/${body["project_id"]}/job_groups/`,
      body,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }
  deleteJobGroups(body: object, project_id: string): Observable<any> {
    return this.http.delete(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/job_groups/`,
      {
        body: body,
        headers: this.getAuthorizationHeader(),
      }
    );
  }
  getJobGroups(project_id: string): Observable<any> {
    return this.http.get(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/job_groups/`,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  getTraceFitPlottingData(project_id: string, body: object): Observable<any> {
    return this.http.put(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/tracefits-plotting/`,
      body,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  getCopilotData(): Observable<any> {
    return this.http.get(`${AppConfig.settings.apiUrl}/copilot`, {
      headers: this.getAuthorizationHeader(),
    });
  }
  getCopilotDataUrls(id: string): Observable<any> {
    return this.http.get(`${AppConfig.settings.apiUrl}/copilot/${id}`, {
      headers: this.getAuthorizationHeader(),
    });
  }
  putCopilotData(formData: FormData): Observable<any> {
    return this.http.put(`${AppConfig.settings.apiUrl}/copilot/`, formData, {
      headers: this.getAuthorizationHeader(),
    });
  }

  createJobs(project_id: string, job_entries: any[]) {
    let job_specs = job_entries.map((jb) => ({
      ...jb,
      project_id: project_id,
    }));
    return this.http.post(
      `${AppConfig.settings.apiUrl_v2}/create-job/`,
      {
        create_project: false,
        job_specifications: job_specs,
      },
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  getAllFields(
    withInfo: boolean = false
  ): Observable<{ fields: Field[]; locations: string[]; clients: string[] }> {
    return this.http.get(
      `${AppConfig.settings.apiUrl}/fields/${
        withInfo ? "?withInfo=" + withInfo : ""
      }`,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  createField(body: NewField): Observable<Field> {
    return this.http.post(`${AppConfig.settings.apiUrl}/fields/`, body, {
      headers: this.getAuthorizationHeader(),
    });
  }

  editField(body: Field): Observable<Field> {
    return this.http.patch(
      `${AppConfig.settings.apiUrl}/fields/${body.id}`,
      body,
      {
        headers: this.getAuthorizationHeader(),
      }
    ) as Observable<Field>;
  }

  getFieldById(id: string): Observable<Field> {
    return this.http.get(`${AppConfig.settings.apiUrl}/fields/${id}`, {
      headers: this.getAuthorizationHeader(),
    });
  }

  getProjectWellLogs(project_id: string): Observable<any> {
    return this.http.get(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/well-logs/`,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  updateWellLogs(project_id: string, data: object): Observable<any> {
    return this.http.post(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/well-logs/`,
      data,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  deleteWellLog(project_id: string, well_log_id: string): Observable<any> {
    return this.http.delete(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/well-logs/`,
      {
        headers: this.getAuthorizationHeader(),
        body: { id: well_log_id },
      }
    );
  }

  getProjectHorizons(project_id: string): Observable<any> {
    return this.http.get(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/horizons/`,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  getProjectHorizonById(
    horizon_Id: string,
    slice_number: number,
    slice_dim: number
  ): Observable<any> {
    return this.http.get(
      `${AppConfig.settings.apiUrl}/projects/horizons/${horizon_Id}?slice_dim=${slice_dim}&slice_number=${slice_number}`,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  updateProjectHorizons(project_id: string, data: object): Observable<any> {
    return this.http.post(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/horizons/`,
      data,
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  deleteProjectHorizons(
    project_id: string,
    horizon_id: string
  ): Observable<any> {
    return this.http.delete(
      `${AppConfig.settings.apiUrl}/projects/${project_id}/horizons/`,
      {
        headers: this.getAuthorizationHeader(),
        body: { id: horizon_id },
      }
    );
  }

  get_xwi_gpt_thread_id(): Observable<any> {
    return this.http.get(`${AppConfig.settings.apiUrl_copilot}/xwi-gpt/`, {
      headers: this.getAuthorizationHeader(),
    });
  }
  prompt_xwi_gpt(prompt: string, thread_id: string): Observable<any> {
    return this.http.post(
      `${AppConfig.settings.apiUrl_copilot}/xwi-gpt/`,
      {
        question: prompt,
        thread_id: thread_id,
      },
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  /**
   * Streams the copilot answer token-by-token via Server-Sent Events.
   *
   * The backend PUT /xwi-gpt/ endpoint returns `text/event-stream`. Angular's
   * HttpClient can't expose a progressive body, so we use fetch() + a
   * ReadableStream reader (which also lets us send the Authorization header,
   * unlike EventSource). Emitted events mirror the backend payloads:
   *   { type: 'token',                data: string }   incremental answer text
   *   { type: 'update',               data: string }   tool-progress note
   *   { type: 'structured_response',  data: object }   image / rag / chart
   *   { type: 'error',                data: string }
   *   { type: 'end' }                                  terminal
   */
  prompt_xwi_gpt_stream(
    prompt: string,
    thread_id: string
  ): Observable<{ type: string; data?: any }> {
    return new Observable((observer) => {
      const controller = new AbortController();

      (async () => {
        try {
          const res = await fetch(
            `${AppConfig.settings.apiUrl_copilot}/xwi-gpt/`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: this.authService.getTokenHeaderText(),
              },
              body: JSON.stringify({ question: prompt, thread_id }),
              signal: controller.signal,
            }
          );

          if (!res.ok || !res.body) {
            throw new Error(`Copilot stream failed (${res.status})`);
          }

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            // SSE frames are separated by a blank line.
            let sep: number;
            while ((sep = buffer.indexOf("\n\n")) !== -1) {
              const frame = buffer.slice(0, sep);
              buffer = buffer.slice(sep + 2);
              const line = frame.split("\n").find((l) => l.startsWith("data:"));
              if (!line) continue;
              const json = line.slice(5).trim();
              if (!json) continue;
              try {
                observer.next(JSON.parse(json));
              } catch {
                // ignore malformed frame
              }
            }
          }
          observer.complete();
        } catch (err) {
          if (!controller.signal.aborted) {
            observer.error(err);
          }
        }
      })();

      // teardown — abort the fetch if the caller unsubscribes
      return () => controller.abort();
    });
  }

  get_spatial_plotting(job_id: string, parameters: object): Observable<any>{
    return this.http.post(
      `${AppConfig.settings.apiUrl}/jobs/${job_id}/spatial_plotting/`, 
      parameters,
      {
        headers: this.getAuthorizationHeader()
      }
    )
  }

  getTraceSourceData(job_id: string, param_string: string){
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/src_plotting/?${param_string}`, {
      headers: this.getAuthorizationHeader(),
    });
  }

  compareModels(data: any): Observable<any> {
    return this.http.post(
      `${AppConfig.settings.apiUrl}/projects/models-compare/`,
      data,
      {
        headers: this.getAuthorizationHeader(),
      }
    ).pipe(
      map((response) => {
        if (!response) return null;
        return response;
      }),
      catchError((err) => {
        console.error('Model comparison error:', err);
        return observableThrowError(err);
      })
    );
  }
  getJobIdFromPath(output_path: string) {
    return this.http.post(
      `${AppConfig.settings.apiUrl}/jobs-output-path/`, 
      {
        output_path: output_path
      },
      {
        headers: this.getAuthorizationHeader()
      }
    )
  }

  checkForNewIterations(job_id: string){
    let randomNumber = Number(Math.random()*100000).toFixed(0);
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/check-iterations/?_t=${randomNumber}`, {
      headers: this.getAuthorizationHeader()
    })
  }
  
  getSavedConversations(): Observable<{[key: string]: any}> {
    return this.http.get(
      `${AppConfig.settings.apiUrl_copilot}/saved-conversations/`,
      {
        headers: this.getAuthorizationHeader(),
      }
    ).pipe(
      map((response) => {
        if (!response) return {};
        return response;
      }),
      catchError((err) => {
        console.error('Failed to load conversations:', err);
        return of({});
      })
    );
  }

  getSavedConversationByKey(key: string): Observable<any> {
    return this.http.get(
      `${AppConfig.settings.apiUrl_copilot}/saved-conversations/`,
      {
        headers: this.getAuthorizationHeader(),
        params: new HttpParams().set("key", key),
      }
    ).pipe(
      map((response) => {
        if (!response) return null;
        return response;
      }),
      catchError((err) => {
        console.error(`Failed to load conversation '${key}':`, err);
        return of(null);
      })
    );
  }

  saveConversation(conversationName: string, messages: any[]): Observable<any> {
    return this.http.post(
      `${AppConfig.settings.apiUrl_copilot}/saved-conversations/`,
      {
        key: conversationName,
        title: conversationName,
        messages: messages
      },
      {
        headers: this.getAuthorizationHeader(),
      }
    );
  }

  renameConversation(conversationKey: string, newTitle: string): Observable<any> {
    return this.http.patch(
      `${AppConfig.settings.apiUrl_copilot}/saved-conversations/`,
      {
        title: newTitle
      },
      {
        headers: this.getAuthorizationHeader(),
        params: new HttpParams().set("key", conversationKey),
      }
    );
  }

  /**
   * Persists changes to a saved conversation's messages (e.g. updated slice/shot
   * request bodies the user tweaked in the visualizers). Uses the same PATCH
   * endpoint as renameConversation — it accepts partial updates, so sending only
   * `messages` leaves the title untouched.
   */
  updateConversationMessages(conversationKey: string, messages: any[]): Observable<any> {
    return this.http.patch(
      `${AppConfig.settings.apiUrl_copilot}/saved-conversations/`,
      {
        messages: messages
      },
      {
        headers: this.getAuthorizationHeader(),
        params: new HttpParams().set("key", conversationKey),
      }
    );
  }

  deleteConversation(conversationKey: string): Observable<any> {
    return this.http.delete(
      `${AppConfig.settings.apiUrl_copilot}/saved-conversations/`,
      {
        headers: this.getAuthorizationHeader(),
        params: new HttpParams().set("key", conversationKey),
      }
    );
  }

  /**
   * Get display variable (min/max) for a specific model type
   * @param project - The project object
   * @param modelType - Model type key (e.g., "Vp", "vdiff", "denchange")
   * @returns Object with min and max values, or null if not set
   */
  getDisplayVariableForModelType(
    project: Project,
    modelType: string
  ): { min: number | null; max: number | null } | null {
    if (!project.displayVariables || !project.displayVariables[modelType]) {
      return null;
    }
    return project.displayVariables[modelType];
  }

  /**
   * Get all display variables for a project
   * @param projectId - UUID of the project
   * @returns Observable of array of display variables
   */
  getDisplayVariablesForProject(projectId: string): Observable<DisplayVariable[]> {
    return this.http
      .get(`${AppConfig.settings.apiUrl}/display-variables/?project_id=${projectId}`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        map((response: any) => {
          // Handle both paginated (results) and non-paginated responses
          return response.results || response;
        })
      );
  }

  /**
   * Update or create a display variable for a specific model type
   * @param projectId - UUID of the project
   * @param modelType - Model type key (e.g., "Vp", "vdiff")
   * @param minValue - Minimum display value (can be null)
   * @param maxValue - Maximum display value (can be null)
   * @returns Observable of the created/updated display variable
   */
  updateDisplayVariable(
    projectId: string,
    modelType: string,
    minValue: number | null,
    maxValue: number | null
  ): Observable<any> {
    // First, get existing display variables for this project
    return this.http
      .get(`${AppConfig.settings.apiUrl}/display-variables/?project_id=${projectId}`, {
        headers: this.getAuthorizationHeader(),
      })
      .pipe(
        switchMap((response: any) => {
          const results = response.results || response; // Handle paginated or non-paginated
          const existing = Array.isArray(results)
            ? results.find((dv: any) => dv.key === modelType)
            : null;

          const payload: any = {
            key: modelType,
            default_min_value: minValue,
            default_max_value: maxValue,
          };
          if (existing) {
            // Update existing
            return this.http.patch(
              `${AppConfig.settings.apiUrl}/display-variables/${existing.id}/`,
              payload,
              { headers: this.getAuthorizationHeader() }
            );
          } else {
            // Create new
            return this.http.post(
              `${AppConfig.settings.apiUrl}/display-variables/`,
              { ...payload, project: projectId },
              { headers: this.getAuthorizationHeader() }
            );
          }
        })
      );
  }

  /**
   * Update multiple display variables at once
   * @param projectId - UUID of the project
   * @param displayVariables - Object mapping model types to min/max values
   * @returns Observable of array of updated/created display variables
   */
  updateDisplayVariables(
    projectId: string,
    displayVariables: { [modelType: string]: { min: number | null; max: number | null } }
  ): Observable<DisplayVariable[]> {
    const updates = Object.keys(displayVariables).map((modelType) =>
      this.updateDisplayVariable(
        projectId,
        modelType,
        displayVariables[modelType].min,
        displayVariables[modelType].max
      )
    );

    return forkJoin(updates);
  }

  /**
   * Delete a display variable
   * @param displayVariableId - UUID of the display variable
   * @returns Observable of the deletion result
   */
  deleteDisplayVariable(displayVariableId: string): Observable<any> {
    return this.http.delete(
      `${AppConfig.settings.apiUrl}/display-variables/${displayVariableId}/`,
      { headers: this.getAuthorizationHeader() }
    );
  }

  getJobProject(id: string, forceRefresh: boolean = false): Observable<any>{
    var url = `${AppConfig.settings.apiUrl}/jobs/${id}/project/`
    if (forceRefresh){
      // this is simply a cache-busting parameter
      let randomNumber = Number(Math.random()*100000).toFixed(0);
      url += `?_t=${randomNumber}`
    }
    
    return this.http.get(
      url,
      { headers: this.getAuthorizationHeader() }
    ).pipe(
      map((response: any) => {
          if (!response) return;
          let data = response.data
          // var projects = 
          const displayVariablesMap: { [key: string]: { min: number | null, max: number | null } } = {};
          if (data.display_variables && Array.isArray(data.display_variables)) {
            data.display_variables.forEach((dv: any) => {
              displayVariablesMap[dv.key] = {
                min: dv.default_min_value,
                max: dv.default_max_value
              };
            });
          }
          data = {
            id: data.id,
            name: data.name,
            description: data.description,
            archiveEnabled: data?.archive_enabled ?? false,
            displayVariables: displayVariablesMap,
            field: data.field,
            jobs: data.jobs.map(jobMapper),
          };

          return this.setProjectDefaults(data);
        })
    )
  }

  getJobOutputLogFiles(job_id: string): Observable<PaginatedResponse<string[]>>{
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/logs/`, { headers: this.getAuthorizationHeader() })
  }
  getJobOutputErrors(job_id: string, filename: string): Observable<PaginatedResponse<LogEntry>>{
    let params = new HttpParams();
    if (filename) {
      params = params.set('filename', filename);
    }
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/logs/errors/`, { headers: this.getAuthorizationHeader(), params })
  }
  getJobOutputErrorsSummary(job_id: string, filename: string): Observable<SummaryLogEntry[]>{
    let params = new HttpParams();
    if (filename) {
      params = params.set('filename', filename);
    }
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/logs/errors/summary/`, { headers: this.getAuthorizationHeader(), params })
  }
  getJobOutputWarnings(job_id: string, filename: string): Observable<PaginatedResponse<LogEntry>>{
    let params = new HttpParams();
    if (filename) {
      params = params.set('filename', filename);
    }
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/logs/warnings/`, { headers: this.getAuthorizationHeader(), params })
  }
  getJobOutputWarningsSummary(job_id: string, filename: string): Observable<SummaryLogEntry[]>{
    let params = new HttpParams();
    if (filename) {
      params = params.set('filename', filename);
    }
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/logs/warnings/summary/`, { headers: this.getAuthorizationHeader(), params })
  }
  downloadJobOutput(job_id: string): Observable<Blob>{
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/logs/download/`, { headers: this.getAuthorizationHeader(), responseType: 'blob' })
  }
  
  getResponse(url: string): Observable<PaginatedResponse<any>>{
    return this.http.get(url, { headers: this.getAuthorizationHeader() })
  }

  calculateJobCost(job_id: string): Observable<any>{
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/cost/`, { headers: this.getAuthorizationHeader()})
  }
  
  getJobTemplate(job_id: string): Observable<any>{
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${job_id}/template/`, {
      headers: this.getAuthorizationHeader()
    })
  }
  getJobConfig(id: string): Observable<any> {
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${id}/template/job-config/`, {
      headers: this.getAuthorizationHeader()
    });
  }

  getRunfileFromPath(id: string, path: string): Observable<any> {
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${id}/template/runfile/?path=${encodeURIComponent(path)}`, {
      headers: this.getAuthorizationHeader()
    })
  }

  getJobEnvSh(id: string): Observable<any> {
    return this.http.get(`${AppConfig.settings.apiUrl}/jobs/${id}/template/env/`, {
      headers: this.getAuthorizationHeader()
    });
  }

  submitJob(projectId: string, data: any): Observable<any> {
    return this.http.post(
      `${AppConfig.settings.apiUrl}/projects/${projectId}/submit-job/`,
      data,
      { headers: this.getAuthorizationHeader() }
    );
  }

  prepJob(projectId: string, data: any): Observable<any> {
    return this.http.post(
      `${AppConfig.settings.apiUrl}/projects/${projectId}/prep-job/`,
      data,
      { headers: this.getAuthorizationHeader() }
    );
  }

  getAuthorizationHeader(): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.append(
      "Authorization",
      this.authService.getTokenHeaderText()
    );
    return headers;
  }

  getJsonDataFromPath(file_path: string): Observable<any> {
    return this.http.get(
      `${AppConfig.settings.apiUrl}/projects/json/${file_path}`,
      { 
        headers: this.getAuthorizationHeader()
      },
    );
  }

}

function jobMapper(j: any) {
  return {
    id: j.id ?? j.job_id,
    name: j.job_name,
    createdDate: new Date(j.created),
    status: j.status,
    minVelocity: null,
    maxVelocity: null,
    minVelocityZ: null,
    maxVelocityZ: null,
    type: j.type,
    owner: j.owner,
    parent_job_id: Object.keys(j).includes("parent_job_id")
      ? j.parent_job_id
      : null,
    comments:
      j.comments != null
        ? j.comments.length == 0
          ? "No comments"
          : j.comments
        : "No comments",
    iterations: j.iterations ?? 1,
    iterationsComplete: j.iterations_completed ?? 1
  };
}

// { headers?: HttpHeaders | { [header: string]: string | string[]; }; observe?: "body"; params?: HttpParams | { [param: string]: string | string[]; }; reportProgress?: boolean; responseType?: "json"; withCredentials?: boolean; }
